import './styles/main.css';
import { createPixelCanvas, MAX_FILL_ASPECT } from './ui/pixelCanvas';
import { addFeat, createInitialState, earnEggs } from './game/state';
import type { GameState } from './game/state';
import {
  buyDino,
  buyShopItem,
  buyUpgrade,
  checkAchievements,
  clickValue,
  dinoLevel,
  epsFromState,
  production,
} from './game/economy';
import { createLoop, TICKS_PER_SECOND } from './game/loop';
import {
  AUTOSAVE_MS,
  collectOffline,
  deserialize,
  load,
  localStorageStore,
  SAVE_KEY,
  mergeOffline,
  offlineProgress,
  saveWithPending,
  serialize,
} from './game/save';
import { isNameRetired, retiredSpelling } from './game/names';
import type { OfflineResult } from './game/save';
import {
  activeBoostMultiplier,
  activeClickMultiplier,
  applyBonus,
  boostSecondsLeft,
  clickBoostSecondsLeft,
  createSpawnTimer,
  expireBoost,
  goldenGap,
  pickBonus,
} from './game/bonuses';
import { BONUSES } from './data/bonuses';
import { METEOR_SPAWN_MAX_SECONDS, METEOR_SPAWN_MIN_SECONDS } from './data/meteors';
import {
  awardShards,
  clickDamage,
  isMeteorsUnlocked,
  meteorGap,
  meteorRate,
  rollMeteorFall,
} from './game/meteors';
import { formatEggSpan, formatEggs } from './game/numbers';
import { createStatsView } from './ui/stats';
import type { BoostStatus } from './ui/stats';
import { COLLECT_FOUNTAIN_TICKS, createNestView } from './ui/nest';
import type { JackpotReveal } from './ui/nest';
import { createDinoCard } from './ui/dinoCard';
import { createHintView } from './ui/hint';
import { DINOS, aOrAn, artSizeOf } from './data/dinos';
import type { DinoDef } from './data/dinos';
import { dinoSpriteUrl, preloadDinoSprites } from './ui/sprites';
import { createShopView } from './ui/shop';
import { createUpgradesView } from './ui/upgrades';
import { createShardShopView } from './ui/shardShop';
import { createInventoryView } from './ui/inventory';
import { createHeldItemFollower, createHoldingBar } from './ui/holding';
import { initItemView } from './ui/itemView';
import {
  eligibility,
  eligibleTargets,
  noTargetHint,
  isInventoryUnlocked,
  speciesName,
  usableItemFor,
  useItemOn,
} from './game/inventory';
import type { ItemDef } from './data/items';
import { createAchievementsView } from './ui/achievements';
import { createExtinctionView } from './ui/extinction';
import { createMuseumView } from './ui/museum';
import {
  buyPerk,
  canExtinct,
  eraModifier,
  expedition,
  expeditionOver,
  extinct,
  fossilMultiplier,
} from './game/rebirth';
import { createTabs } from './ui/tabs';
import { createToasts } from './ui/toasts';
import { createWelcomeView } from './ui/welcome';
import { createSettingsView } from './ui/settings';
import { createDisclosures } from './ui/disclosures';
import { createRulesView } from './ui/rules';
import { createShareView } from './ui/share';
import { createLifetimeStatsView } from './ui/lifetimeStats';
import { createSound } from './ui/sound';
import { createConfirm } from './ui/confirm';
import { initAnalytics, track } from './analytics';
import { runInOneTab } from './ui/oneTab';
import { liftLoaderWhen } from './ui/loader';
import { createBossFight } from './ui/bossFight';
import { BOSS_FIGHTS_LIVE, DEINOSUCHUS, VICTORY_ROAR_SECONDS } from './data/bosses';
import { settleFight } from './game/boss';

// M5: the composition root. State lives here and is reassignable, so import
// and wipe can swap in a new one; every UI module reads state through the
// closures below rather than holding its own reference.

/** The falling-egg debug shortcut is available in dev builds or with ?debug. */
const DEBUG = import.meta.env.DEV || new URLSearchParams(window.location.search).has('debug');

function requireElement(id: string): HTMLElement {
  const node = document.getElementById(id);
  if (!node) throw new Error(`#${id} not found`);
  return node;
}

/** What the one-tab guard needs from a running game. */
interface RunningGame {
  /** Save, stop, and never save again: another tab is taking over. */
  standDown(): void;
}

function main(): RunningGame {
  // False once another tab has taken over (ui/oneTab.ts): this page's game
  // is stale from then on, and any save it made would overwrite the new one.
  let playing = true;
  const canvasEl = requireElement('nest');
  if (!(canvasEl instanceof HTMLCanvasElement)) {
    throw new Error('#nest is not a canvas');
  }

  const store = localStorageStore();
  const saved = load(store);
  // A save that is there but cannot be read (a newer version met by an old
  // cached page, or damage) would be overwritten by the first autosave. Put
  // it aside first, so it can still be recovered.
  const unreadable = saved === null ? store.get() : null;
  if (unreadable) localStorageStore(`${SAVE_KEY}-unreadable-${Date.now()}`).set(unreadable);
  let state: GameState = saved ?? createInitialState();

  const pc = createPixelCanvas(canvasEl);
  const sound = createSound();
  const toasts = createToasts(requireElement('toasts'));
  const welcome = createWelcomeView(document.body);

  /** Effective EPS right now: upgrades, achievements, and any active boost. */
  const currentEps = (): number => epsFromState(state, activeBoostMultiplier(state, Date.now()));
  /**
   * EPS without any golden-egg boost. Milestones and unlocks key off this so
   * a temporary x5 can't hand out an eggs-per-second achievement (or open an
   * upgrade) the player hasn't actually reached.
   */
  const baseEps = (): number => epsFromState(state);

  // Falling eggs ----------------------------------------------------------------

  const spawnTimer = createSpawnTimer();

  /** Grants a caught egg's bonus; a jackpot's amount is returned for the yard to show. */
  function catchFallingEgg(): JackpotReveal | null {
    const bonus = pickBonus(Math.random());
    const eps = currentEps();
    const result = applyBonus(bonus, state, eps, Date.now());
    switch (result.kind) {
      case 'boost':
      case 'clickBoost':
        sound.play('crack');
        toasts.show(bonus.name, `${bonus.description} Go go go!`);
        return null;
      case 'jackpot': {
        sound.play('crack');
        const amount = formatEggs(result.eggs);
        toasts.show(bonus.name, `The egg cracks open: ${amount} eggs!`);
        const span = formatEggSpan(result.eggs, eps);
        return {
          amount: `+${amount}`,
          caption: span ? `${bonus.name} · ${span} of eggs` : bonus.name,
        };
      }
      case 'whiff':
        sound.play('whiff');
        toasts.show(`${bonus.name}... whiff`, 'A stronger boost is already running.');
        return null;
    }
  }

  preloadDinoSprites();
  const nest = createNestView(pc, {
    onEggClick: () => {
      const gained = clickValue(state, currentEps()) * activeClickMultiplier(state, Date.now());
      state.clicks++;
      earnEggs(state, gained);
      sound.play('click');
      return gained;
    },
    onFallingEggCaught: catchFallingEgg,
    onDinoClick: (id) => {
      const item = inventory.armed;
      const def = DINOS.find((d) => d.id === id);
      if (item && def && eligibility(item, def, state).ok) {
        void useSpliceOn(item, def);
        return;
      }
      dinoCard.open(id);
    },
    meteorDamage: () => clickDamage(state),
    onMeteorIncoming: () => sound.play('falling'),
    onMeteorImpact: () => sound.play('thud'),
    onMeteorHit: () => sound.play('clink'),
    onMeteorBroken: (meteor) => {
      // Broken in the last second of its countdown: Last Second.
      if (meteor.secondsLeft < 1) addFeat(state, 'last-second');
      const shards = awardShards(state, meteor);
      sound.play('crash');
      track('meteor_broken', { kind: meteor.kind.id, shards });
      return shards;
    },
    onShowerCleared: () => addFeat(state, 'shower'),
  });
  /** Hatch or level up a tier; a new species hatching plays out on the egg. */
  function hatchOrLevelUp(def: DinoDef): void {
    const wasHatched = dinoLevel(def, state) > 0;
    if (!buyDino(def, state)) return;
    sound.play('buy');
    if (!wasHatched) {
      // A new era's herd starts unnamed: names go with the dinosaurs at an
      // extinction, and the one the player excavates keeps its name on a
      // Museum plaque.
      nest.hatch(def.id);
      // How far players get, and how fast: a new tier hatched by the player.
      // Tiers the game hatches for them (the Hatchery perk, the Cretaceous
      // era) do not come through here and are not counted.
      track('hatch', {
        dino: def.id,
        tier: def.tier,
        era: state.era + 1,
        minutes: Math.round((Date.now() - state.eraStartedAt) / 60000),
      });
      saveNow();
    }
  }

  // The card lays over the yard panel.
  const nestPanelEl = canvasEl.parentElement ?? document.body;
  const hint = createHintView(nestPanelEl);
  // Boss fights: the arena takes the whole screen; the result is settled
  // into the save here, and the fight never waits on the network.
  const bossFight = createBossFight(document.body, {
    onDone: (fight, def) => {
      const now = Date.now();
      const outcome = settleFight(state, fight, def.id, now);
      track('boss', {
        boss: fight.boss.id,
        dino: def.id,
        won: outcome.won,
        flawless: outcome.flawless,
        seconds: Math.round(fight.tick / 20),
      });
      saveNow();
      return outcome;
    },
    play: (s) => sound.play(s),
  });

  const dinoCard = createDinoCard(nestPanelEl, {
    onEvolve: (def) => {
      const item = usableItemFor(def, state);
      if (item) useSpliceOn(item, def);
    },
    onRename: (def, name) => {
      // A name on a Museum plaque belongs to the one in the case.
      if (name && isNameRetired(state, name)) {
        return { ok: false, taken: retiredSpelling(state, name) ?? name };
      }
      if (name) state.names[def.id] = name;
      else delete state.names[def.id];
      return { ok: true };
    },
    onChallenge: (def) => {
      if (!BOSS_FIGHTS_LIVE) return;
      dinoCard.close();
      bossFight.start(DEINOSUCHUS, def, state);
    },
  });

  function spawnFallingEgg(): void {
    if (nest.hasFallingEgg || welcome.open || nest.extincting || bossFight.open) return;
    nest.spawnFallingEgg();
    sound.play('chime');
  }

  // Meteors --------------------------------------------------------------------

  const meteorTimer = createSpawnTimer(
    Math.random,
    METEOR_SPAWN_MIN_SECONDS,
    METEOR_SPAWN_MAX_SECONDS,
  );
  let meteorsWereUnlocked = isMeteorsUnlocked(state);
  // The meteor's recorded fall loads once meteors are on, well before the
  // first one drops (2-5 minutes after they unlock).
  if (meteorsWereUnlocked) sound.preloadSamples();

  function spawnMeteor(): void {
    if (nest.hasMeteor || welcome.open || nest.extincting || bossFight.open) return;
    nest.spawnMeteor(rollMeteorFall(state));
  }

  if (DEBUG) {
    window.addEventListener('keydown', (ev: KeyboardEvent) => {
      if (ev.key === 'F' && ev.shiftKey) spawnFallingEgg();
      if (ev.key === 'M' && ev.shiftKey) spawnMeteor();
    });
  }

  // Panels ----------------------------------------------------------------------

  const stats = createStatsView({
    eggCount: requireElement('egg-count'),
    eps: requireElement('eps'),
    clickValue: requireElement('click-value'),
    boost: requireElement('boost'),
  });
  // The game's own confirm, in place of the browser's.
  const ask = createConfirm(document.body);

  /** Uses an armed splice on a dinosaur, once the player has seen both forms and agreed. */
  async function useSpliceOn(item: ItemDef, def: DinoDef): Promise<void> {
    const species = speciesName(def, state);
    const pet = state.names[def.id];
    const who = pet ? `"${pet}" the ${species}` : `your ${species}`;
    const size = artSizeOf(def, 'full');
    const ok = await ask({
      title: `Evolve ${pet ? `"${pet}"` : `your ${species}`}?`,
      art: [
        { src: dinoSpriteUrl(def.id, 'full'), size, label: species },
        { src: dinoSpriteUrl(def.evolved.id, 'full'), size, label: def.evolved.name },
      ],
      body: `Use your ${item.name} to evolve ${who} into ${aOrAn(def.evolved.name)} ${def.evolved.name}.`,
      note: `${def.evolved.flavor} Its eggs per second triple.`,
      confirm: 'Evolve',
      cancel: 'Not yet',
      tone: 'evolve',
    });
    if (!ok) return;
    if (!useItemOn(item, def, state)) return;
    inventory.disarm();
    // The card lays over the yard, so it has to go before the transformation
    // plays underneath it — evolving from the card is the common way in.
    dinoCard.close();
    nest.playEvolution(def.id);
    sound.play('upgrade');
    toasts.show(
      `${pet ? `"${pet}"` : `Your ${species}`} is now ${aOrAn(def.evolved.name)} ${def.evolved.name}`,
      `${def.evolved.flavor} Its eggs per second tripled.`,
    );
    track('evolve', { dino: def.id, into: def.evolved.id, item: item.id });
    saveNow();
  }

  const shop = createShopView(requireElement('dino-shop'), hatchOrLevelUp, useSpliceOn);
  const upgrades = createUpgradesView(requireElement('upgrade-shop'), (def) => {
    if (buyUpgrade(def, state, baseEps())) sound.play('upgrade');
  });
  // Panel heights per layout (see .layout in main.css):
  // - wide: the side panel's height is fixed to its tab strip plus seven rows
  //   of achievement tiles (whichever tab is open), so nothing jumps on a tab
  //   switch; the yard is a quarter taller than that, unless the window is
  //   narrow enough that its width caps it at 4:3.
  // - medium: the yard keeps a 4:3 aspect from its width (the canvas sets the
  //   panel's height); the shop and the side panel, stacked beside it, each
  //   take half of the viewport below the header.
  // - phone: everything is auto height and the page scrolls.
  const sidePanel = requireElement('side-panel');
  // The left column holds the stats panel and the scrolling dinosaur list;
  // capping the column is what makes the list scroll inside itself.
  const shopColumn = requireElement('shop-panel').parentElement;
  const layoutEl = requireElement('layout');
  const nestPanel = requireElement('nest').parentElement;
  function matchPanelHeights(): void {
    if (!nestPanel) return;
    const mode = getComputedStyle(layoutEl).getPropertyValue('--layout').trim();
    // Viewport height below the header, less the page's bottom padding.
    const naturalTop = layoutEl.getBoundingClientRect().top + window.scrollY;
    const avail = Math.max(400, window.innerHeight - naturalTop - 16);
    if (mode === 'medium') {
      nestPanel.style.height = '';
      const half = Math.floor((avail - 16) / 2);
      if (shopColumn) {
        shopColumn.style.height = `${half}px`;
        shopColumn.style.maxHeight = '';
      }
      sidePanel.style.height = `${half}px`;
      return;
    }
    if (shopColumn) {
      shopColumn.style.height = '';
      // The list scrolls inside its panel; the column must not push the page
      // into scrolling.
      shopColumn.style.maxHeight = mode === 'wide' ? `${avail}px` : '';
    }
    const fixed = getComputedStyle(nestPanel).getPropertyValue('--fill-height').trim() === '1';
    if (!fixed) {
      sidePanel.style.height = '';
      nestPanel.style.height = '';
      return;
    }
    const px = (v: string): number => parseFloat(v) || 0;
    const strip = requireElement('side-tabs');
    const grid = sidePanel.querySelector<HTMLElement>('.achievements__grid');
    const count = sidePanel.querySelector<HTMLElement>('.achievements__count');
    const cs = getComputedStyle(sidePanel);
    const scs = getComputedStyle(strip);
    // The count line and grid are read from their CSS (fixed height and
    // max-height) rather than measured, since the tab may be hidden.
    const ccs = count ? getComputedStyle(count) : null;
    const inner =
      strip.offsetHeight +
      px(scs.marginTop) +
      px(scs.marginBottom) +
      (ccs ? px(ccs.height) + px(ccs.marginTop) + px(ccs.marginBottom) : 0) +
      (grid ? px(getComputedStyle(grid).maxHeight) : 0);
    const total =
      inner +
      px(cs.paddingTop) +
      px(cs.paddingBottom) +
      px(cs.borderTopWidth) +
      px(cs.borderBottomWidth);
    sidePanel.style.height = `${Math.round(total)}px`;
    // The yard is a quarter taller than the side panel; the extra is ground.
    // On a narrower window that height would make the yard nearly square,
    // so it is capped by the yard's own width (the 24 is the panel padding).
    const availW = nestPanel.clientWidth - 24;
    const byWidth = Math.floor(availW * MAX_FILL_ASPECT) + 24;
    nestPanel.style.height = `${Math.min(Math.round(total * 1.25), byWidth)}px`;
  }

  // Icons or rows for the item panels (ui/itemView.ts), as last chosen.
  initItemView();
  createTabs(requireElement('side-tabs'), [
    { label: 'Upgrades', panel: requireElement('upgrade-shop') },
    { label: 'Achievements', panel: requireElement('achievements') },
  ]);
  // The left panel holds all the spending: the dinosaur list and, once
  // meteors are unlocked, the shard shop.
  const shopTabs = createTabs(
    requireElement('shop-tabs'),
    [
      { label: 'Dinosaurs', panel: requireElement('dino-shop') },
      { label: 'Shop', panel: requireElement('shard-shop') },
      { label: 'Inventory', panel: requireElement('inventory') },
      { label: 'Museum', panel: requireElement('museum') },
    ],
    'shop',
  );
  const SHARD_TAB = 1;
  const INVENTORY_TAB = 2;
  const MUSEUM_TAB = 3;
  const heldItem = createHeldItemFollower();
  const inventory = createInventoryView(requireElement('inventory'), {
    onArm: (item) => {
      shop.setTargetMode(item);
      // The item rides beside the pointer while it is held.
      heldItem.show(item);
      // Picked up on the Inventory tab, used on the Dinosaurs one: go there.
      if (item) shopTabs.select(0);
      refreshHolding();
    },
  });
  // What is held, on every tab, with a way to put it down (ui/holding.ts).
  const holding = createHoldingBar(requireElement('shop-tabs'), () => inventory.disarm());
  function refreshHolding(): void {
    const item = inventory.armed;
    const targets = item ? eligibleTargets(item, state) : [];
    holding.update(item, targets.length, item ? noTargetHint(item, state) : '');
    nest.setEvolveTargets(item ? new Set(targets.map((d) => d.id)) : null);
  }
  // Escape puts a held item down, unless a dialog is open and wants it.
  window.addEventListener('keydown', (ev) => {
    if (ev.key !== 'Escape' || !inventory.armed) return;
    if (document.querySelector('.modal:not([hidden])')) return;
    inventory.disarm();
  });
  const shardShop = createShardShopView(requireElement('shard-shop'), (def) => {
    if (buyShopItem(def, state)) {
      sound.play('upgrade');
      track('shop_buy', { item: def.id, price: def.price });
      saveNow();
    }
  });
  // The Extinction ends the era: the yard plays it out and calls back from
  // inside the impact flash, which is when the state is swapped.
  const extinction = createExtinctionView(
    requireElement('extinction'),
    requireElement('era-line'),
    (choice) => {
      if (!canExtinct(state) || nest.extincting) return;
      const before = state;
      sound.play('crash');
      nest.playExtinction(() => {
        // An import or a wipe during the animation replaced the game; ending
        // the old one now would put it back over the new.
        if (state !== before) return;
        const done = extinct(before, Date.now(), choice.excavated, choice.eraId, choice.expedition);
        state = done.state;
        saveNow();
        const era = eraModifier(state);
        toasts.show(
          `Era ${before.era + 1} ended`,
          `+${done.fossils} fossil${done.fossils === 1 ? '' : 's'}. Everything they lay from here is worth ${Math.round(fossilMultiplier(state) * 100)}% of what it was.`,
        );
        if (era) toasts.show(`The ${era.name} era begins`, era.description);
        const trip = expedition(state);
        if (trip) toasts.show(`Expedition: ${trip.name}`, trip.description);
        track('extinct', {
          era: before.era + 1,
          fossils: done.fossils,
          eraId: choice.eraId ?? 'plain',
          excavated: choice.excavated.length,
        });
      });
    },
  );

  const museum = createMuseumView(requireElement('museum'), (def) => {
    if (!buyPerk(def, state)) return;
    sound.play('upgrade');
    toasts.show(def.name, `${def.description} ${def.cost} fossils spent.`);
    track('perk_buy', { perk: def.id, cost: def.cost });
    saveNow();
  });

  const achievements = createAchievementsView(requireElement('achievements'));
  // Lays the panels out and refits the yard; a refit clears the canvas, so
  // the yard is redrawn in the same moment rather than left blank until the
  // next frame.
  function relayout(): void {
    matchPanelHeights();
    if (pc.fit()) nest.render({ dinos: state.dinos, state });
  }
  relayout();
  window.addEventListener('resize', relayout);
  // The yard's panel can change width without the window resizing. On a
  // 390 px phone it measured 70 px wider on first load than once the page had
  // settled, so the yard was sized for 2× and the browser squeezed it to fit
  // — non-integer scaled pixel art — until the phone was rotated. Watching
  // the panel itself refits whenever its width actually changes. Width only:
  // on a phone the panel's height follows the canvas, so refitting on height
  // would chase its own tail.
  if (nestPanel && 'ResizeObserver' in window) {
    let fittedWidth = nestPanel.clientWidth;
    new ResizeObserver(() => {
      const width = nestPanel.clientWidth;
      if (width === fittedWidth) return;
      fittedWidth = width;
      relayout();
    }).observe(nestPanel);
  }

  // Persistence -------------------------------------------------------------

  // Every 30 s while on screen, on hiding or closing the tab, and straight
  // after the moments that would hurt most to lose to a crash: an extinction,
  // an evolution, a perk or shop item bought, a new tier hatched. So there is
  // no Save button; there is nothing for it to add.
  let pending: OfflineResult | null = null;
  /**
   * Eggs just collected that the counter is still rolling up to (the
   * welcome-back haul): it shows the balance less what has not "arrived"
   * yet, easing out.
   */
  let countUp: { eggs: number; start: number; ms: number } | null = null;
  const shownEggs = (): number => {
    if (!countUp) return state.eggs;
    const t = (performance.now() - countUp.start) / countUp.ms;
    if (t >= 1) {
      countUp = null;
      return state.eggs;
    }
    const eased = 1 - (1 - t) * (1 - t);
    return Math.max(0, state.eggs - countUp.eggs * (1 - eased));
  };
  // Eggs still waiting on the welcome-back panel go into the stored copy, so
  // a tab killed with the panel open does not lose them.
  const saveNow = (): void => {
    if (playing) saveWithPending(state, store, pending);
  };

  /**
   * Back from time away. The eggs are paid when the player collects them
   * rather than when they are measured, so the total does not already include
   * them while the panel is still asking. Anything too small for the panel is
   * paid straight away.
   */
  const cameBack = (result: OfflineResult): void => {
    // A second spell away can arrive while the first is still on screen: the
    // tab becoming visible measures again, and a browser that restores tabs
    // does exactly that a moment after the page loads. Fold it into what is
    // waiting rather than paying it out — paying here credited the whole time
    // away while the panel was still asking, which is the bug this set out to
    // fix in the first place.
    const combined = pending ? mergeOffline(pending, result) : result;
    pending = null;
    if (!combined.notable) {
      collectOffline(state, combined);
      return;
    }
    pending = combined;
    // show() replaces the panel's close handler, so only this one can pay.
    welcome.show(combined, () => {
      // The haul pours off the egg in a fountain, and the eggs counter
      // counts up to the new total as it does.
      const eggs = combined.eggs;
      collectOffline(state, combined);
      nest.collectEggs(eggs);
      if (eggs > 0) {
        sound.play('click');
        countUp = {
          eggs,
          start: performance.now(),
          ms: (COLLECT_FOUNTAIN_TICKS * 1000) / TICKS_PER_SECOND,
        };
      }
      pending = null;
      saveNow();
    });
  };

  // Leaving with the panel still open should not cost the player the time:
  // autosave would otherwise write a state that has moved lastSaved on
  // without the eggs that move bought.
  window.addEventListener('pagehide', () => {
    if (!pending || !playing) return;
    collectOffline(state, pending);
    pending = null;
    saveNow();
  });

  const settingsBody = createSettingsView({
    sound,
    exportSave: () => serialize(state),
    importSave: (encoded) => {
      const loaded = deserialize(encoded);
      if (!loaded) return false;
      state = loaded;
      // Its meteors may already be unlocked; that is not news.
      meteorsWereUnlocked = isMeteorsUnlocked(state);
      if (meteorsWereUnlocked) sound.preloadSamples();
      cameBack(offlineProgress(state));
      saveNow();
      return true;
    },
    wipe: () => {
      store.remove();
      state = createInitialState();
      meteorsWereUnlocked = false;
    },
  });
  const lifetimeStats = createLifetimeStatsView();
  const RULES_READ_MS = 5000;
  let rulesTimer = 0;
  createDisclosures(requireElement('settings'), [
    { label: 'Settings', body: settingsBody },
    {
      label: 'Rules',
      body: createRulesView(),
      // By the Book: the Rules left open long enough to read some of them,
      // so opening and shutting them straight away does not count.
      onOpen: () => {
        rulesTimer = window.setTimeout(() => addFeat(state, 'rules'), RULES_READ_MS);
      },
      onClose: () => window.clearTimeout(rulesTimer),
    },
    { label: 'Stats', body: lifetimeStats.body },
    {
      label: 'Share',
      body: createShareView(
        () => ({
          totalEggs: state.totalEggs,
          era: state.era,
          fossils: state.eraLog.at(-1)?.fossils ?? 0,
        }),
        // The next tick's achievement check awards Word of Mouth.
        () => {
          state.shared = true;
        },
      ),
    },
  ]);

  // No autosave while hidden: the loop is stopped there, so a save would only
  // move lastSaved forward and swallow the time away.
  window.setInterval(() => {
    if (document.visibilityState === 'visible') saveNow();
  }, AUTOSAVE_MS);
  // pagehide is the reliable "tab is going away" signal, including on mobile;
  // visibilitychange covers backgrounding, where the tab may be killed later.
  window.addEventListener('pagehide', saveNow);
  document.addEventListener('visibilitychange', () => {
    if (!playing) return;
    if (document.visibilityState === 'hidden') {
      saveNow();
      return;
    }
    // Back from a hidden tab: rAF stopped while it was hidden, so treat the
    // gap like time with the game closed (credited at the offline rate),
    // then drop the loop's backlog so it doesn't also replay it.
    const back = offlineProgress(state);
    loop.resync();
    cameBack(back);
  });

  // Offline progress ----------------------------------------------------------

  cameBack(offlineProgress(state));

  // Loop ----------------------------------------------------------------------

  /** The running boosts (production and click), for the countdown bars. */
  function boostStatuses(now: number): BoostStatus[] {
    const out: BoostStatus[] = [];
    for (const [slot, secondsLeft] of [
      [state.boost, boostSecondsLeft(state, now)],
      [state.clickBoost, clickBoostSecondsLeft(state, now)],
    ] as const) {
      if (!slot) continue;
      // A boss win's Victory Roar is a boost with no golden egg behind it.
      if (slot.kind === 'roar') {
        out.push({
          name: 'Victory Roar',
          label: `×${slot.mult}`,
          secondsLeft,
          totalSeconds: VICTORY_ROAR_SECONDS,
        });
        continue;
      }
      const def = BONUSES.find(
        (b) =>
          (b.effect.kind === 'boost' || b.effect.kind === 'clickBoost') &&
          b.effect.boost === slot.kind,
      );
      if (!def || def.effect.kind === 'jackpot') continue;
      out.push({
        name: def.name,
        label: def.effect.kind === 'clickBoost' ? `clicks ×${slot.mult}` : `×${slot.mult}`,
        secondsLeft,
        totalSeconds: def.effect.seconds,
      });
    }
    return out;
  }

  /**
   * The panels round the yard (shop, upgrades, achievements, the extinction
   * bar, Stats) refresh ten times a second rather than every frame: in a
   * profile of a late-game page they were a fifth of every frame, for text
   * that changes a few times a second at most. Any click or key press makes
   * the next frame refresh them, so a purchase still shows at once; the click
   * is marked in the capture phase, before its handler runs, and the frame
   * after it sees the result.
   */
  const PANEL_REFRESH_MS = 100;
  let panelsAt = -Infinity;
  let panelsDirty = true;
  const markPanels = (): void => {
    panelsDirty = true;
  };
  document.addEventListener('click', markPanels, true);
  document.addEventListener('keydown', markPanels, true);

  const loop = createLoop({
    tick(seconds) {
      const now = Date.now();
      expireBoost(state, now);
      const eps = currentEps();
      const gained = production(eps, seconds);
      earnEggs(state, gained);
      nest.tick();

      // The spawn clock only runs during visible, unobstructed play.
      const golden = goldenGap(state);
      if (
        golden &&
        document.visibilityState === 'visible' &&
        !welcome.open &&
        !bossFight.open &&
        !nest.hasFallingEgg
      ) {
        spawnTimer.setRange(golden.min, golden.max);
        if (spawnTimer.advance(seconds)) spawnFallingEgg();
      }

      for (const earned of checkAchievements(state, baseEps())) {
        sound.play('achievement');
        toasts.show(`Achievement: ${earned.name}`, earned.description);
      }

      // Meteors fall once unlocked, on their own clock, under the same rules
      // as the falling egg (visible play only, one at a time).
      const unlocked = isMeteorsUnlocked(state);
      if (unlocked && !meteorsWereUnlocked) {
        sound.preloadSamples();
        toasts.show(
          'Something is streaking across the sky…',
          'Meteors will start falling into the yard. Break them for shards.',
        );
      }
      meteorsWereUnlocked = unlocked;
      const sky = meteorGap(state);
      if (
        sky &&
        unlocked &&
        document.visibilityState === 'visible' &&
        !welcome.open &&
        !bossFight.open &&
        !nest.hasMeteor
      ) {
        meteorTimer.setRange(sky.min, sky.max);
        if (meteorTimer.advance(seconds * meteorRate(state))) spawnMeteor();
      }
    },
    render() {
      const eps = currentEps();
      nest.render({ dinos: state.dinos, state });
      const perClick = clickValue(state, eps) * activeClickMultiplier(state, Date.now());
      stats.update(shownEggs(), eps, perClick, boostStatuses(Date.now()));
      // A Sprint ends itself: the rule is the whole point, so it does not
      // wait to be told.
      // Not behind the welcome-back panel: the eggs it holds belong to this
      // era, and an extinction swapped in first had them paid into the next.
      if (
        !nest.extincting &&
        !welcome.open &&
        expeditionOver(state, Date.now()) &&
        canExtinct(state)
      ) {
        extinction.callIt();
      }
      // The yard and the egg count above run every frame; the panels below
      // at most every PANEL_REFRESH_MS, or at once after a click or a key.
      const now = performance.now();
      if (!panelsDirty && now - panelsAt < PANEL_REFRESH_MS) return;
      panelsDirty = false;
      panelsAt = now;
      hint.update(state);
      const meteorsOn = isMeteorsUnlocked(state);
      shopTabs.setHidden(SHARD_TAB, !meteorsOn);
      // The Inventory tab appears with the first splice bought and stays,
      // empty or not, so the player knows where their items live.
      shopTabs.setHidden(INVENTORY_TAB, !isInventoryUnlocked(state));
      // The Museum opens as soon as an extinction is possible, a step before
      // the first fossil exists: ending an era is a harder decision than it
      // needs to be while what fossils buy is still hidden. It stays after,
      // since that is where the fossil balance lives.
      const museumOpen = canExtinct(state) || state.fossilsEarned > 0;
      shopTabs.setHidden(MUSEUM_TAB, !museumOpen);
      if (museumOpen) museum.update(state);
      inventory.update(state);
      refreshHolding();
      if (meteorsOn) shardShop.update(state);
      lifetimeStats.update(state, eps, perClick);
      extinction.update(state);
      shop.update(state, activeBoostMultiplier(state, Date.now()));
      dinoCard.update(state, activeBoostMultiplier(state, Date.now()));
      upgrades.update(state, baseEps());
      achievements.update(state);
    },
  });
  loop.start();
  // The loading screen stays up until the yard can be drawn as itself.
  liftLoaderWhen(() => nest.artReady);

  return {
    standDown() {
      saveNow();
      playing = false;
      loop.stop();
    },
  };
}

initAnalytics();
runInOneTab(main);
